import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import api from '../../api/api';
import Loader from '../loader/Loader';
import Pagination from 'react-js-pagination';
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import Video from "yet-another-react-lightbox/plugins/video";
import No_Data from '../../utils/zero-state-screens/No_Orders.svg';
import './my-complaints.css';
import { MdSignalWifiConnectedNoInternet0 } from 'react-icons/md';
import { ValidateNoInternet } from '../../utils/NoInternetValidator';
import { Link, useNavigate } from 'react-router-dom';

const MyComplaints = () => {
    const { t } = useTranslation();
    const token = useSelector(state => state.user?.jwtToken);
    const navigate = useNavigate()
    const [complaints, setComplaints] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    const [currentComplaintMedia, setCurrentComplaintMedia] = useState([]);
    const [isNetworkError, setIsNetworkError] = useState(false);
    const [totalComplaints, setTotalComplaints] = useState(0);
    const [offset, setOffset] = useState(0);
    const [currPage, setCurrPage] = useState(1);
    const total_complaints_per_page = 10;

    const fetchComplaints = async () => {
        try {
            setIsLoading(true);
            const response = await api.getUserComplaints(token, total_complaints_per_page, offset);
            const result = await response.json();
            if (result?.status === 1) {
                setComplaints(result?.data);
                setTotalComplaints(result?.total);
            } else if (result?.message === "No complaints found") {
                setComplaints([]);
            }
        } catch (err) {
            console.error('Error fetching complaints:', err);
            const isNoInternet = ValidateNoInternet(err);
            if (isNoInternet) {
                setIsNetworkError(true);
            } else {
                toast.error(t('error_fetching_complaints'));
            }
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchComplaints();
    }, [offset]);

    const handlePageChange = (pageNum) => {
        setCurrPage(pageNum);
        setOffset(pageNum * total_complaints_per_page - total_complaints_per_page);
    };

    const openLightbox = (complaint, startIndex = 0) => {
        // Create slides for lightbox
        const slides = [
            ...(complaint.images || []).map(img => ({
                src: img,
            })),
            ...(complaint.videos || []).map(video => ({
                type: "video",
                sources: [
                    {
                        src: video,
                        type: "video/mp4",
                    }
                ]
            }))
        ];

        setCurrentComplaintMedia(slides);
        setLightboxIndex(startIndex);
        setLightboxOpen(true);
    };

    return (
        <>
            {!isNetworkError ? (
                <div className='my-complaints-container'>
                    <div className='heading'>
                        {t("my_complaints")}
                    </div>

                    {isLoading ? (
                        <div className='my-5'><Loader width='100%' height='500px' /></div>
                    ) : (
                        <>
                            <div className="my-complaints-list">
                                {complaints && complaints.length === 0 ? (
                                    <div className='d-flex flex-column align-items-center p-4 my-complaints-no-data'>
                                        <img src={No_Data} className='no-data-img' alt='no-complaints' />
                                        <p>{t("no_complaints_found")}</p>
                                    </div>
                                ) : (
                                    <div className="my-complaint-items-container">
                                        {complaints.map((complaint) => (
                                            <Link to={`/profile/orders/${complaint.order_id}`} key={complaint.id} className="my-complaint-item-link my-complaint-item">
                                                <div className="my-complaint-item-header">
                                                    <div className="header-info" >
                                                        <h3>{t("id")} #{complaint.id}</h3>
                                                        <p>{new Date(complaint.created_at).toLocaleDateString("en-GB", {
                                                            day: "2-digit",
                                                            month: "short",
                                                            year: "numeric",
                                                        })}</p>
                                                    </div>
                                                    {complaint?.status === 'pending' && (
                                                        <div className="my-complaint-item-status">
                                                            <p className='pending'>{t('pending')}</p>
                                                        </div>
                                                    )}
                                                    {complaint?.status === "under_review" && (
                                                        <div className="my-complaint-item-status">
                                                            <p className='under_review'>{t('under_review')}</p>
                                                        </div>
                                                    )}
                                                    {complaint?.status === "resolved" && (
                                                        <div className="my-complaint-item-status">
                                                            <p className='resolved'>{t('resolved')}</p>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className='my-complaint-item-body'>
                                                    <p className="my-complaint-description">{complaint?.description}</p>
                                                    {complaint?.images?.length > 0 && (
                                                        <div className="my-complaint-item-images" onClick={(e) => e.preventDefault()}>
                                                            {complaint?.images?.slice(0, complaint?.images?.length > 3 || complaint?.videos?.length > 0 ? 3 : 4).map((image, index, array) => (
                                                                <div
                                                                    key={index}
                                                                    className="my-complaint-image-wrapper"
                                                                >
                                                                    <img
                                                                        src={image}
                                                                        className='my-complaint-image'
                                                                        alt="Complaint"
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            openLightbox(complaint, index);
                                                                        }}
                                                                    />
                                                                    {/* Show overlay on the last visible image only if there are more images or videos */}
                                                                    {(index === array.length - 1 && (complaint?.images?.length > 3 || complaint?.videos?.length > 0)) && (
                                                                        <div
                                                                            className="my-complaint-image-overlay"
                                                                            onClick={(e) => {
                                                                                e.preventDefault();
                                                                                openLightbox(complaint, 0);
                                                                            }}
                                                                        >
                                                                            <span>+{(complaint?.images?.length - 3) + (complaint?.videos?.length || 0)}</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {complaint?.videos?.length > 0 && complaint?.images?.length === 0 && (
                                                        <div className="my-complaint-item-videos" onClick={(e) => e.preventDefault()}>
                                                            {complaint?.videos?.slice(0, 3).map((video, index, array) => (
                                                                <div
                                                                    key={index}
                                                                    className="my-complaint-video-wrapper"
                                                                >
                                                                    <video
                                                                        src={video}
                                                                        alt="Complaint"
                                                                        className='my-complaint-video'
                                                                        controls
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            openLightbox(complaint, complaint?.images?.length + index);
                                                                        }}
                                                                    />
                                                                    {/* Show overlay on the last visible video only if there are more videos */}
                                                                    {(index === array.length - 1 && complaint?.videos?.length > 3) && (
                                                                        <div
                                                                            className="my-complaint-video-overlay"
                                                                            onClick={(e) => {
                                                                                e.preventDefault();
                                                                                openLightbox(complaint, complaint?.images?.length);
                                                                            }}
                                                                        >
                                                                            <span>+{complaint?.videos?.length - 3}</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {complaint?.images?.length === 0 && complaint?.videos?.length === 0 && (
                                                        <div className='my-complaint-item-no-media' />
                                                    )}
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {complaints && complaints.length > 0 && totalComplaints > total_complaints_per_page && (
                                <Pagination
                                    itemClass='my-complaints-page-item'
                                    activePage={currPage}
                                    itemsCountPerPage={total_complaints_per_page}
                                    totalItemsCount={totalComplaints}
                                    pageRangeDisplayed={5}
                                    onChange={handlePageChange}
                                />
                            )}
                        </>
                    )}
                </div>
            ) : (
                <div className='d-flex flex-column justify-content-center align-items-center my-complaints-no-internet'>
                    <MdSignalWifiConnectedNoInternet0 />
                    <p>{t("no_internet_connection")}</p>
                </div>
            )}

            <Lightbox
                open={lightboxOpen}
                close={() => setLightboxOpen(false)}
                index={lightboxIndex}
                slides={currentComplaintMedia}
                plugins={[Video]}
            />
        </>
    );
};

export default MyComplaints;